import { toast } from "sonner";
import axios from "axios";
import FileDropzone from "../../components/FileDropzone";
import { useEffect, useState } from "react";
import { FileText, Plus, ShieldCheck, Sparkles, X, CheckCircle2, ChevronDown } from "lucide-react";
import { API, money, abs, Empty } from "./shared";
import { ChatPanel } from "./ChatPanel";
import { BriefDeliveryPanel } from "./DesignDelivery";
import { AgencyQuoteForm, BuyerQuotesPanel, BuyerFixedHirePanel } from "./DesignQuotes";
import { ProofingPanel, DeliveryPanel } from "./Proofing";

// ============================================================
// VIEW: STUDIO PROFILE (agency)
// ============================================================
export function StudioProfile({ supplierId }) {
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploads, setUploads] = useState([]);
  useEffect(() => {
    axios.get(`${API}/agencies/${supplierId}`).then(r => setProfile(r.data)).catch(() => setProfile(false));
  }, [supplierId]);

  if (profile === null) return <section className="content"><Empty text="Loading studio profile…"/></section>;
  if (profile === false) return <section className="content"><Empty text="No profile yet — creating one requires backend seed. Ping admin."/></section>;

  const CAPS = ["3D Dieline CAD", "Graphic Layout", "Branding", "Labeling"];
  const TIERS = ["Free", "Pro", "Enterprise"];
  const toggleCap = (c) => {
    const next = profile.design_capabilities.includes(c)
      ? profile.design_capabilities.filter(x => x !== c)
      : [...profile.design_capabilities, c];
    setProfile({ ...profile, design_capabilities: next });
  };
  const save = async () => {
    setSaving(true);
    await axios.patch(`${API}/agencies/${supplierId}`, {
      portfolio_file_ids: uploads.map(u => u.id),
      design_capabilities: profile.design_capabilities,
      base_rate_usd: Number(profile.base_rate_usd),
      subscription_tier: profile.subscription_tier,
      tagline: profile.tagline,
    });
    const { data } = await axios.get(`${API}/agencies/${supplierId}`);
    setProfile(data);
    setUploads([]);
    setSaving(false);
  };

  return (
    <section className="content">
      <div className="section-heading">
        <div>
          <p className="eyebrow">STUDIO PROFILE</p>
          <h2>{profile._user?.name}</h2>
          <p className="subhead">This is what buyers see when matching briefs. Keep your portfolio fresh and your capabilities accurate.</p>
        </div>
        <div className={`tier-badge tier-${profile.subscription_tier?.toLowerCase()}`} data-testid="tier-badge">
          <Sparkles size={13}/> {profile.subscription_tier} plan
        </div>
      </div>

      <div className="studio-form">
        <label>Tagline
          <input data-testid="tagline-input" value={profile.tagline || ""} onChange={e => setProfile({ ...profile, tagline: e.target.value })}/>
        </label>
        <label>Base design rate (INR)
          <input data-testid="base-rate-input" type="number" min="0" value={profile.base_rate_usd || 0} onChange={e => setProfile({ ...profile, base_rate_usd: e.target.value })}/>
        </label>
        <label>Subscription tier
          <select data-testid="tier-select" value={profile.subscription_tier} onChange={e => setProfile({ ...profile, subscription_tier: e.target.value })}>
            {TIERS.map(t => <option key={t}>{t}</option>)}
          </select>
        </label>

        <div className="modal-full-row">
          <label className="dz-label">Design capabilities</label>
          <div className="cap-pills">
            {CAPS.map(c => (
              <button key={c} type="button" data-testid={`studio-cap-${c.replace(/\s+/g, "-").toLowerCase()}`} className={`cap-pill ${profile.design_capabilities?.includes(c) ? "active" : ""}`} onClick={() => toggleCap(c)}>{c}</button>
            ))}
          </div>
        </div>

        <div className="modal-full-row">
          <label className="dz-label">Portfolio ({profile.portfolio_urls?.length || 0} pieces)</label>
          <div className="portfolio-grid">
            {(profile.portfolio_urls || []).map((u, i) => (
              <a key={i} href={abs(u)} target="_blank" rel="noreferrer" className="portfolio-tile" data-testid={`portfolio-${i}`}>
                <img src={abs(u)} alt={`portfolio ${i}`} onError={(e) => { e.currentTarget.style.display = "none"; }}/>
              </a>
            ))}
          </div>
          <FileDropzone
            testid="portfolio-dropzone"
            scope="artwork"
            accept="image/*"
            value={uploads}
            onChange={setUploads}
            label="Add portfolio pieces"
            hint="JPG · PNG · WebP — highlights the aesthetic buyers see when matching briefs"
          />
        </div>

        <div className="form-actions">
          <button data-testid="studio-save-button" className="primary-btn" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save changes"} <ShieldCheck size={15}/>
          </button>
        </div>
      </div>

      {/* Phase A — Designer specialties, rate card & tier badge */}
      <DesignerRateCard userId={supplierId}/>
    </section>
  );
}

// ============================================================
// PHASE A · Designer specialties, tier badge & rate card (agency)
// ============================================================
export function DesignerRateCard({ userId }) {
  const [ref, setRef] = useState(null);
  const [me, setMe] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    axios.get(`${API}/design/reference`).then(r => setRef(r.data)).catch(() => setRef({ specialties: [], tiers: [] }));
    axios.get(`${API}/users/${userId}`).then(r => setMe({
      design_specialties: r.data.design_specialties || [],
      revision_rate_inr: r.data.revision_rate_inr || 0,
      fixed_rates: r.data.fixed_rates || {},
      design_tier: r.data.design_tier || null,
    })).catch(() => setMe({ design_specialties: [], revision_rate_inr: 0, fixed_rates: {}, design_tier: null }));
  }, [userId]);
  if (!ref || !me) return null;
  const toggle = (code) => {
    const next = me.design_specialties.includes(code)
      ? me.design_specialties.filter(x => x !== code)
      : [...me.design_specialties, code];
    setMe({ ...me, design_specialties: next });
  };
  const tierMeta = ref.tiers.find(t => t.code === me.design_tier);
  const save = async () => {
    setSaving(true); setErr("");
    try {
      await axios.patch(`${API}/design/designers/${userId}/profile`, {
        design_specialties: me.design_specialties,
        revision_rate_inr: Number(me.revision_rate_inr) || 0,
        fixed_rates: Object.fromEntries(
          Object.entries(me.fixed_rates).map(([k, v]) => [k, Number(v) || 0])
        ),
      });
      toast.success("Rate card saved");
    } catch (e) {
      setErr(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };
  return (
    <div className="rate-card" data-testid="designer-rate-card">
      <div className="section-heading" style={{marginTop:24}}>
        <div>
          <p className="eyebrow">DESIGN SERVICES · PHASE A</p>
          <h2>Specialties & rate card</h2>
          <p className="subhead">Tag your design specialties and post fixed project prices plus your paid-revision rate. Every project includes 5 free revisions; your revision rate applies after that.</p>
        </div>
        {tierMeta ? (
          <span className={`dtier-badge dtier-${tierMeta.code}`} data-testid="designer-tier-badge">
            <ShieldCheck size={13}/> {tierMeta.label}
          </span>
        ) : (
          <span className="dtier-badge dtier-none" data-testid="designer-tier-badge">Awaiting admin tier tag</span>
        )}
      </div>
      <div className="studio-form">
        <div className="modal-full-row">
          <label className="dz-label">Design specialties</label>
          <div className="cap-pills" data-testid="designer-specialty-pills">
            {ref.specialties.map(s => (
              <button
                key={s.code}
                type="button"
                data-testid={`designer-spec-${s.code}`}
                className={`cap-pill ${me.design_specialties.includes(s.code) ? "active" : ""}`}
                onClick={() => toggle(s.code)}
              >{s.label}</button>
            ))}
          </div>
        </div>
        <label>Paid revision rate (INR per round, after 5 free)
          <input
            data-testid="designer-revision-rate"
            type="number"
            min="0"
            value={me.revision_rate_inr}
            onChange={e => setMe({...me, revision_rate_inr: e.target.value})}
          />
        </label>
        <div/>
        <div className="modal-full-row">
          <label className="dz-label">Fixed rates per specialty (INR)</label>
          <div className="rate-grid">
            {ref.specialties.map(s => (
              <label key={s.code} className="rate-row">
                <span>{s.label}</span>
                <input
                  data-testid={`designer-fixed-${s.code}`}
                  type="number"
                  min="0"
                  placeholder="0"
                  value={me.fixed_rates[s.code] ?? ""}
                  onChange={e => setMe({...me, fixed_rates: {...me.fixed_rates, [s.code]: e.target.value}})}
                />
              </label>
            ))}
          </div>
        </div>
        {err && <div className="modal-full-row" style={{color:"#b91c1c",fontSize:12}} data-testid="designer-rate-err">{err}</div>}
        <div className="form-actions">
          <button data-testid="designer-rate-save" className="primary-btn" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save specialties & rates"} <ShieldCheck size={15}/>
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// PHASE A · Design services hub (brief posting + inbox)
// ============================================================
const BRIEF_STATUS = {
  open: ["live", "Open for bids"], in_progress: ["matching", "In progress"], approved: ["completed", "Approved"],
  delivered: ["completed", "Delivered"], completed: ["completed", "Completed"], closed: ["closed", "Closed"],
};

function BriefRow({ b, svcLabel, onOpen }) {
  const [cls, label] = BRIEF_STATUS[b.status] || ["closed", b.status];
  const awarded = !!b.awarded_agency_id;
  return (
    <button data-testid={`design-brief-row-${b.id}`} className="job-row table-row" onClick={onOpen}>
      <div className="job-title">
        <div className="job-symbol">DS</div>
        <div><b>{b.title}</b><span>{b.id} · {b.hire_mode === "fixed" ? "Fixed hire" : "Bidding"}</span></div>
      </div>
      <div className="spec">
        <b>{svcLabel}</b>
        <span>{awarded ? `Fixed ${money(b.awarded_price_inr)}` : b.budget_inr ? `Budget ${money(b.budget_inr)}` : "No budget"}{b.deadline_days ? ` · ${b.deadline_days} days` : ""}</span>
      </div>
      <div>
        <span className={`status ${cls}`} data-testid={`design-brief-status-${b.id}`}>{label}</span>
        {b.dispute_status === "open" && <div style={{fontSize:11,color:"#B45309",marginTop:4}}>Dispute open</div>}
      </div>
      <div className="activity" data-testid={`design-brief-activity-${b.id}`}>
        {awarded ? (
          <><b>{b.awarded_agency_name || "Agency"}</b><span>{Math.min(b.revisions_used || 0, b.free_revisions || 5)}/{b.free_revisions || 5} free revisions used{b.escrow_status === "held" ? " · Escrow held" : ""}</span></>
        ) : (
          <><b>{b.quote_count || 0} bids</b><span>Posted {new Date(b.created_at).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" })}</span></>
        )}
      </div>
      <ChevronDown size={16} className="row-arrow"/>
    </button>
  );
}

export function DesignServices({ role, userId, user, focusBriefId, onFocusHandled }) {
  const [ref, setRef] = useState(null);
  const [briefs, setBriefs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("active");
  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/design/briefs`);
      setBriefs(r.data.briefs || []);
      return r.data.briefs || [];
    } catch (e) {
      setBriefs([]);
      return [];
    } finally { setLoading(false); }
  };
  useEffect(() => {
    axios.get(`${API}/design/reference`).then(r => setRef(r.data)).catch(() => setRef({specialties:[],tiers:[],categories:[],service_types:[]}));
    load();
  }, []); // eslint-disable-line

  // Auto-open a specific brief when caller navigates in with a focusBriefId
  // (e.g. from Supplier Network → Request hire). Clears after opening.
  useEffect(() => {
    if (!focusBriefId || !ref) return;
    (async () => {
      let list = briefs;
      if (!list.some(b => b.id === focusBriefId)) list = await load();
      const target = list.find(b => b.id === focusBriefId);
      if (target) {
        setSelected(target);
        // Jump to the right tab so the card is actually visible behind the drawer.
        const completedStatuses = ["delivered", "approved", "closed", "completed"];
        if (target.awarded_agency_id) {
          setTab(completedStatuses.includes(target.status) ? "completed" : "awarded");
        } else {
          setTab("active");
        }
      }
      onFocusHandled && onFocusHandled();
    })();
  }, [focusBriefId, ref]); // eslint-disable-line

  const specLabel = (code) => ref?.specialties?.find(s => s.code === code)?.label || code;
  const tierLabel = (code) => ref?.tiers?.find(t => t.code === code)?.label || code;

  // Partition into Active (open, awaiting award) vs Awarded (in-flight) vs Completed (delivered or beyond).
  // Splitting Awarded from Completed keeps the live-work queue tight and gives buyers a permanent archive
  // of ratings, downloads and copyright certificates for shipped briefs.
  const COMPLETED_STATUSES = ["delivered", "approved", "closed", "completed"];
  const activeBriefs = briefs.filter(b => !b.awarded_agency_id && b.status === "open");
  const awardedBriefs = briefs.filter(b => !!b.awarded_agency_id && !COMPLETED_STATUSES.includes(b.status));
  const completedBriefs = briefs.filter(b => !!b.awarded_agency_id && COMPLETED_STATUSES.includes(b.status));
  const visibleBriefs = tab === "awarded"
    ? awardedBriefs
    : tab === "completed"
      ? completedBriefs
      : (role === "buyer" ? activeBriefs : briefs.filter(b => !b.awarded_agency_id));

  const svcLabelFor = (b) => {
    const svc = (ref?.service_types || []).find(x => (x.code || x) === b.service_type);
    const base = svc ? (svc.label || svc.code || svc) : (b.service_type === "preflight" ? "Pre-flight / DTP" : "Custom design");
    return b.preferred_specialty ? `${base} · ${specLabel(b.preferred_specialty)}` : base;
  };
  return (
    <section className="content">
      <div className="section-heading">
        <div>
          <p className="eyebrow">DESIGN SERVICES · PHASE A</p>
          <h2>{role === "agency" ? "Open briefs for you" : role === "admin" ? "All design briefs" : "Your design briefs"}</h2>
          <p className="subhead">
            {role === "buyer" && "Post a structured design brief — category, dimensions, CMYK, brand kit — and receive quotes from tier-verified agencies. In-flight work stays on Awarded; shipped work moves to Completed with ratings and copyright certificates waiting there."}
            {role === "agency" && "Structured briefs from buyers, filtered against your specialties. Update specialties in Studio profile to widen your inbox."}
            {role === "admin" && "Every posted design brief across the platform. Manage agency tier tags in the Supplier network."}
          </p>
        </div>
        {role === "buyer" && (
          <button data-testid="design-brief-new-button" className="primary-btn" onClick={() => setShowForm(true)}>
            <Plus size={16}/> Post a design brief
          </button>
        )}
      </div>

      <div data-testid="design-services-tabs" style={{display:"flex",gap:8,borderBottom:"1px solid #E5E7EB",marginBottom:18,marginTop:-6}}>
        {[
          { id: "active", label: "Active briefs", count: activeBriefs.length },
          { id: "awarded", label: "Awarded briefs", count: awardedBriefs.length },
          { id: "completed", label: "Completed", count: completedBriefs.length },
        ].map(t => (
          <button
            key={t.id}
            data-testid={`design-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            style={{
              background:"transparent",
              border:"none",
              padding:"10px 4px",
              marginRight:14,
              cursor:"pointer",
              color: tab === t.id ? "#111827" : "#6B7280",
              fontWeight: tab === t.id ? 700 : 500,
              fontSize: 14,
              borderBottom: tab === t.id ? "2px solid #111827" : "2px solid transparent",
              marginBottom:"-1px",
              display:"inline-flex",
              alignItems:"center",
              gap:8,
            }}
          >
            {t.label}
            <span style={{
              background: tab === t.id ? "#111827" : "#E5E7EB",
              color: tab === t.id ? "#FACC15" : "#374151",
              padding:"2px 8px",
              borderRadius:999,
              fontSize:10,
              fontWeight:700,
              minWidth:20,
              textAlign:"center",
            }}>{t.count}</span>
          </button>
        ))}
      </div>

      {loading ? <Empty text="Loading briefs…"/> : (
        role === "buyer" ? (
          <div className="job-table" data-testid="design-brief-list">
            <div className="table-head">
              <span>REQUEST</span><span>SPECIFICATION</span><span>STATUS</span><span>ACTIVITY</span><span></span>
            </div>
            {visibleBriefs.length === 0 ? (
            <Empty text={
              tab === "completed"
                ? (role === "buyer" ? "No completed briefs yet. Once your agency delivers final artwork, the brief lands here with ratings, downloads and copyright certificates." : "No completed briefs yet.")
                : tab === "awarded"
                  ? (role === "buyer" ? "No awarded briefs in flight. Pick an agency on an open brief to kick off work — it will land here with NDA, chat and the delivery panel." : "No awarded briefs in flight.")
                  : (role === "buyer" ? "No open briefs. Post one above to receive quotes from verified agencies." : "No open briefs match right now. Check back soon.")
            }/>
            ) : visibleBriefs.map(b => <BriefRow key={b.id} b={b} svcLabel={svcLabelFor(b)} onOpen={() => setSelected(b)}/>)}
          </div>
        ) : (
        <div className="brief-grid" data-testid="design-brief-list">
          {visibleBriefs.length === 0 ? (
            <Empty text={
              tab === "completed"
                ? (role === "buyer" ? "No completed briefs yet. Once your agency delivers final artwork, the brief lands here with ratings, downloads and copyright certificates." : "No completed briefs yet.")
                : tab === "awarded"
                  ? (role === "buyer" ? "No awarded briefs in flight. Pick an agency on an open brief to kick off work — it will land here with NDA, chat and the delivery panel." : "No awarded briefs in flight.")
                  : (role === "buyer" ? "No open briefs. Post one above to receive quotes from verified agencies." : "No open briefs match right now. Check back soon.")
            }/>
          ) : visibleBriefs.map(b => (
            <button
              key={b.id}
              data-testid={`design-brief-card-${b.id}`}
              className="brief-card"
              onClick={() => setSelected(b)}
              style={{textAlign:"left",cursor:"pointer"}}
            >
              <div className="brief-card-top">
                <span className={`status ${b.status === "open" ? "live" : b.status === "in_progress" ? "matching" : "completed"}`}>{b.status}</span>
                <span style={{font:"10px 'DM Mono'",color:"#94a3b8"}}>{b.hire_mode === "fixed" ? "FIXED HIRE" : "BIDDING"}</span>
              </div>
              <h3>{b.title}</h3>
              <p>{(() => {
                const svc = (ref?.service_types || []).find(s => (s.code || s) === b.service_type);
                return svc ? (svc.label || svc.code || svc) : (b.service_type === "preflight" ? "Pre-flight / DTP" : "Custom design");
              })()}{b.preferred_specialty ? ` · ${specLabel(b.preferred_specialty)}` : ""}</p>
              <div className="brief-details">
                {b.awarded_agency_id ? <span>✅ {b.awarded_agency_name || b.awarded_agency_id} · {money(b.awarded_price_inr)}</span> : null}
                {!b.awarded_agency_id && b.budget_inr ? <span>💰 {money(b.budget_inr)}</span> : null}
                {b.deadline_days ? <span>⏱ {b.deadline_days} days</span> : null}
                {b.preferred_tier ? <span>★ {tierLabel(b.preferred_tier)}</span> : null}
                {b.escrow_status === "held" ? <span>🔒 Escrow held</span> : null}
              </div>
              <div className="brief-footer">
                <em>{new Date(b.created_at).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" })}</em>
                <span>View →</span>
              </div>
            </button>
          ))}
        </div>
        )
      )}

      {showForm && ref && (
        <DesignBriefForm
          ref_={ref}
          close={() => setShowForm(false)}
          onCreated={() => { setShowForm(false); load(); }}
        />
      )}
      {selected && ref && (
        <DesignBriefDrawer
          brief={selected}
          ref_={ref}
          role={role}
          userId={userId}
          user={user}
          close={() => setSelected(null)}
          refresh={load}
        />
      )}
    </section>
  );
}

export function DesignBriefForm({ ref_, close, onCreated }) {
  const firstCategoryCode = (ref_.categories[0] && (ref_.categories[0].code || ref_.categories[0])) || "packaging_box";
  const [f, setF] = useState({
    title: "",
    category: firstCategoryCode,
    service_type: "custom",
    hire_mode: "bidding",
    dimensions: { length: "", width: "", height: "", unit: "cm" },
    color_scheme: "",
    cmyk_values: "",
    text_content: "",
    brand_guidelines: "",
    budget_inr: "",
    deadline_days: "",
    preferred_specialty: "",
    preferred_tier: "",
  });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!f.title || f.title.length < 3) { setErr("Title must be at least 3 characters"); return; }
    setBusy(true); setErr("");
    try {
      const payload = {
        title: f.title,
        category: f.category,
        service_type: f.service_type,
        hire_mode: f.hire_mode,
        dimensions: (f.dimensions.length || f.dimensions.width || f.dimensions.height) ? {
          length: Number(f.dimensions.length) || null,
          width: Number(f.dimensions.width) || null,
          height: Number(f.dimensions.height) || null,
          unit: f.dimensions.unit,
        } : null,
        color_scheme: f.color_scheme || null,
        cmyk_values: f.cmyk_values || null,
        text_content: f.text_content || null,
        brand_guidelines: f.brand_guidelines || null,
        reference_file_ids: files.map(x => x.id),
        budget_inr: Number(f.budget_inr),
        deadline_days: f.deadline_days ? Number(f.deadline_days) : null,
        preferred_specialty: f.preferred_specialty || null,
        preferred_tier: f.preferred_tier || null,
      };
      await axios.post(`${API}/design/briefs`, payload);
      onCreated();
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not post brief");
    } finally { setBusy(false); }
  };
  return (
    <div className="drawer-backdrop" onClick={close}>
      <aside className="drawer" onClick={e => e.stopPropagation()}>
        <button data-testid="brief-form-close" className="close-btn" onClick={close}><X size={18}/></button>
        <div className="drawer-top">
          <p className="eyebrow">NEW DESIGN BRIEF</p>
          <h2>Post a structured brief</h2>
          <p style={{color:"#64748b",font:"12px Outfit",marginTop:6}}>Give agencies everything they need to quote confidently.</p>
        </div>
        <form onSubmit={submit} className="studio-form" style={{maxWidth:"none"}}>
          <label style={{gridColumn:"span 2"}}>Brief title
            <input data-testid="brief-title-input" required minLength={3} value={f.title} onChange={e => setF({...f, title: e.target.value})} placeholder="e.g. 250g coffee stand-up pouch — matte finish"/>
          </label>
          <label>Category
            <select data-testid="brief-category-select" value={f.category} onChange={e => setF({...f, category: e.target.value})}>
              {ref_.categories.map(c => {
                const code = c.code || c;
                const label = c.label || code.replace(/_/g, " ");
                return <option key={code} value={code}>{label}</option>;
              })}
            </select>
          </label>
          <label>Service type
            <select data-testid="brief-service-type-select" value={f.service_type} onChange={e => setF({...f, service_type: e.target.value})}>
              {ref_.service_types.map(s => {
                const code = s.code || s;
                const label = s.label || (code === "preflight" ? "Pre-flight / DTP correction" : code === "custom" ? "Custom design" : code.replace(/_/g, " "));
                return <option key={code} value={code}>{label}</option>;
              })}
            </select>
          </label>
          <div className="modal-full-row">
            <label className="dz-label">Hire mode</label>
            <div className="hire-mode-row" data-testid="brief-hire-mode">
              <label className={`hire-opt ${f.hire_mode === "bidding" ? "active" : ""}`}>
                <input type="radio" name="hire_mode" value="bidding" checked={f.hire_mode === "bidding"} onChange={() => setF({...f, hire_mode: "bidding"})} data-testid="brief-hire-mode-bidding"/>
                <b>Reverse bidding</b>
                <span>Agencies submit competing quotes. Award the best one.</span>
              </label>
              <label className={`hire-opt ${f.hire_mode === "fixed" ? "active" : ""}`}>
                <input type="radio" name="hire_mode" value="fixed" checked={f.hire_mode === "fixed"} onChange={() => setF({...f, hire_mode: "fixed"})} data-testid="brief-hire-mode-fixed"/>
                <b>Fixed-rate hire</b>
                <span>Pick a specific agency from their rate card & fund escrow now.</span>
              </label>
            </div>
          </div>
          <label>Preferred specialty (optional)
            <select data-testid="brief-preferred-specialty-select" value={f.preferred_specialty} onChange={e => setF({...f, preferred_specialty: e.target.value})}>
              <option value="">Any specialty</option>
              {ref_.specialties.map(s => <option key={s.code} value={s.code}>{s.label}</option>)}
            </select>
          </label>
          <label>Preferred tier (optional)
            <select data-testid="brief-preferred-tier-select" value={f.preferred_tier} onChange={e => setF({...f, preferred_tier: e.target.value})}>
              <option value="">Any tier</option>
              {ref_.tiers.map(t => <option key={t.code} value={t.code}>{t.label}</option>)}
            </select>
          </label>
          <label>Your budget (INR) *
            <input data-testid="brief-budget-input" type="number" min="1" required value={f.budget_inr} onChange={e => setF({...f, budget_inr: e.target.value})} placeholder="e.g. 5000"/>
          </label>
          <label>Deadline (days)
            <input data-testid="brief-deadline-input" type="number" min="1" value={f.deadline_days} onChange={e => setF({...f, deadline_days: e.target.value})} placeholder="e.g. 7"/>
          </label>
          <div className="modal-full-row">
            <label className="dz-label">Dimensions</label>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 90px",gap:8}}>
              <input data-testid="brief-dim-length" placeholder="Length" type="number" value={f.dimensions.length} onChange={e => setF({...f, dimensions: {...f.dimensions, length: e.target.value}})} style={{padding:10,border:"1px solid var(--line)",borderRadius:5}}/>
              <input data-testid="brief-dim-width" placeholder="Width" type="number" value={f.dimensions.width} onChange={e => setF({...f, dimensions: {...f.dimensions, width: e.target.value}})} style={{padding:10,border:"1px solid var(--line)",borderRadius:5}}/>
              <input data-testid="brief-dim-height" placeholder="Height" type="number" value={f.dimensions.height} onChange={e => setF({...f, dimensions: {...f.dimensions, height: e.target.value}})} style={{padding:10,border:"1px solid var(--line)",borderRadius:5}}/>
              <select data-testid="brief-dim-unit" value={f.dimensions.unit} onChange={e => setF({...f, dimensions: {...f.dimensions, unit: e.target.value}})} style={{padding:10,border:"1px solid var(--line)",borderRadius:5}}>
                <option value="cm">cm</option>
                <option value="mm">mm</option>
                <option value="in">in</option>
              </select>
            </div>
          </div>
          <label>Color scheme
            <input data-testid="brief-color-scheme" value={f.color_scheme} onChange={e => setF({...f, color_scheme: e.target.value})} placeholder="e.g. Earth tones — sage + terracotta"/>
          </label>
          <label>CMYK values
            <input data-testid="brief-cmyk" value={f.cmyk_values} onChange={e => setF({...f, cmyk_values: e.target.value})} placeholder="e.g. C: 45, M: 30, Y: 65, K: 5"/>
          </label>
          <label style={{gridColumn:"span 2"}}>Text content on artwork
            <textarea data-testid="brief-text-content" rows={2} value={f.text_content} onChange={e => setF({...f, text_content: e.target.value})} placeholder="Product name, tagline, ingredients, batch info…" style={{font:"13px Outfit",padding:10,border:"1px solid var(--line)",borderRadius:5,color:"var(--ink)"}}/>
          </label>
          <label style={{gridColumn:"span 2"}}>Brand guidelines
            <textarea data-testid="brief-brand-guidelines" rows={2} value={f.brand_guidelines} onChange={e => setF({...f, brand_guidelines: e.target.value})} placeholder="Fonts, mandatory logos, do's & don'ts…" style={{font:"13px Outfit",padding:10,border:"1px solid var(--line)",borderRadius:5,color:"var(--ink)"}}/>
          </label>
          <div className="modal-full-row">
            <label className="dz-label">Reference files (brand kit, logos, moodboard)</label>
            <FileDropzone
              testid="brief-reference-dropzone"
              scope="artwork"
              accept="image/*,application/pdf,.ai,.eps,.zip"
              value={files}
              onChange={setFiles}
              label="Add reference files"
              hint="Logos, moodboards, PDF references — up to 100 MB each"
            />
          </div>
          {err && <div className="modal-full-row" style={{color:"#b91c1c",fontSize:12}} data-testid="brief-form-err">{err}</div>}
          <div className="form-actions" style={{gridColumn:"span 2"}}>
            <button type="button" className="secondary-btn" onClick={close}>Cancel</button>
            <button data-testid="brief-form-submit" type="submit" className="primary-btn" disabled={busy}>
              {busy ? "Posting…" : "Post brief"} <Sparkles size={15}/>
            </button>
          </div>
        </form>
      </aside>
    </div>
  );
}

export function DesignBriefDrawer({ brief: initial, ref_, role, userId, user, close, refresh }) {
  const [brief, setBrief] = useState(initial);
  const [quotes, setQuotes] = useState([]);
  const [payErr, setPayErr] = useState("");
  const specLabel = (code) => ref_?.specialties?.find(s => s.code === code)?.label || code;
  const tierLabel = (code) => ref_?.tiers?.find(t => t.code === code)?.label || code;
  const reloadBrief = async () => {
    try {
      const { data } = await axios.get(`${API}/design/briefs/${initial.id}`);
      setBrief(data);
    } catch (e) { /* ignore */ }
  };
  const loadQuotes = async () => {
    try {
      const { data } = await axios.get(`${API}/design/briefs/${initial.id}/quotes`);
      setQuotes(data.quotes || []);
    } catch (e) { setQuotes([]); }
  };
  useEffect(() => { reloadBrief(); loadQuotes(); }, [initial.id]); // eslint-disable-line

  const isBuyerOwner = role === "buyer" && brief.buyer_id === userId;
  const canShowQuoteList = role === "admin" || isBuyerOwner || role === "agency";
  const centered = brief.hire_mode === "fixed";

  return (
    <div className={`drawer-backdrop${centered ? " centered" : ""}`} onClick={close}>
      <aside className={`drawer${centered ? " centered" : ""}`} onClick={e => e.stopPropagation()}>
        <button data-testid="brief-drawer-close" className="close-btn" onClick={close}><X size={18}/></button>
        <div className="drawer-top">
          <p className="eyebrow">DESIGN BRIEF · {brief.id}</p>
          <h2>{brief.title}</h2>
          <div className="drawer-status-row">
            <span className={`status ${brief.status === "open" ? "live" : brief.status === "in_progress" ? "matching" : "completed"}`}>{brief.status}</span>
            <span className="status matching">{brief.hire_mode === "fixed" ? "Fixed-rate hire" : "Reverse bidding"}</span>
            <span className="status matching">{(() => {
              const svc = (ref_?.service_types || []).find(s => (s.code || s) === brief.service_type);
              return svc ? (svc.label || svc.code || svc) : (brief.service_type === "preflight" ? "Pre-flight" : "Custom design");
            })()}</span>
            <span className="status matching">{(() => {
              const cat = (ref_?.categories || []).find(c => (c.code || c) === brief.category);
              return cat ? (cat.label || cat.code || cat) : brief.category.replace(/_/g, " ");
            })()}</span>
            {brief.escrow_status === "held" && <span className="status completed" data-testid="brief-escrow-held">🔒 Escrow held</span>}
          </div>
        </div>
        <div className="detail-block">
          {brief.awarded_agency_id && (
            <>
              <label>Awarded to</label><b data-testid="brief-awarded-agency">{brief.awarded_agency_name || brief.awarded_agency_id}</b>
              <label>Fixed project price</label><b data-testid="brief-awarded-price">{money(brief.awarded_price_inr)}</b>
              <label>Revisions</label><b data-testid="brief-revision-terms">5 free · then {brief.revision_rate_inr ? money(brief.revision_rate_inr) : "—"}/round</b>
              {brief.awarded_delivery_days ? <><label>Delivery</label><b>{brief.awarded_delivery_days} days</b></> : null}
            </>
          )}
          {brief.preferred_specialty && <><label>Preferred specialty</label><b>{specLabel(brief.preferred_specialty)}</b></>}
          {brief.preferred_tier && <><label>Preferred tier</label><b>{tierLabel(brief.preferred_tier)}</b></>}
          {brief.budget_inr ? <><label>Budget</label><b>{money(brief.budget_inr)}</b></> : null}
          {brief.deadline_days ? <><label>Deadline</label><b>{brief.deadline_days} days</b></> : null}
          {brief.dimensions && (brief.dimensions.length || brief.dimensions.width) && (
            <><label>Dimensions</label><p>{brief.dimensions.length || "—"} × {brief.dimensions.width || "—"} × {brief.dimensions.height || "—"} {brief.dimensions.unit}</p></>
          )}
          {brief.color_scheme && <><label>Colour scheme</label><p>{brief.color_scheme}</p></>}
          {brief.cmyk_values && <><label>CMYK</label><p>{brief.cmyk_values}</p></>}
          {brief.text_content && <><label>Artwork copy</label><p>{brief.text_content}</p></>}
          {brief.brand_guidelines && <><label>Brand guidelines</label><p>{brief.brand_guidelines}</p></>}
          {(brief.reference_urls || []).length > 0 && (
            <>
              <label>Reference files</label>
              <div className="asset-list" data-testid="brief-reference-list">
                {brief.reference_urls.map((u, i) => (
                  <a key={i} href={abs(u)} target="_blank" rel="noreferrer" className="asset-row">
                    <div className="asset-icon"><FileText size={16}/></div>
                    <div className="asset-info"><b>Reference {i+1}</b><span>Open in new tab</span></div>
                    <span className="asset-download">Download ↗</span>
                  </a>
                ))}
              </div>
            </>
          )}
          <label>Posted by</label><b>{brief.buyer_name || brief.buyer_id}</b>
          <label>Posted</label><p>{new Date(brief.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}</p>
        </div>

        {payErr && <div style={{padding:12,margin:"8px 0",background:"#FEF2F2",border:"1px solid #FCA5A5",color:"#991B1B",borderRadius:6,fontSize:12}} data-testid="brief-pay-err">{payErr}</div>}

        {/* AGENCY: submit / update quote (bidding only, brief open) */}
        {role === "agency" && brief.status === "open" && brief.hire_mode === "fixed" && (
          <div className="drawer-callout" data-testid="brief-agency-fixed-notice" style={{background:"#F0F6FF",border:"1px solid #BFDBFE",padding:14,borderRadius:8,display:"flex",gap:10,alignItems:"flex-start",marginTop:16}}>
            <Sparkles size={16}/>
            <div><b>Buyer prefers fixed-rate hire.</b><span style={{display:"block",fontSize:12,color:"#1E40AF",marginTop:4}}>They may hire directly from rate cards — you can still submit your fixed project price and paid revision rate below.</span></div>
          </div>
        )}
        {role === "agency" && brief.status === "open" && (
          <AgencyQuoteForm brief={brief} close={close} refresh={() => { loadQuotes(); refresh(); }}/>
        )}
        {role === "agency" && brief.awarded_agency_id === userId && (
          <div className="drawer-callout ok" data-testid="brief-agency-won" style={{background:"#ECFDF5",border:"1px solid #6EE7B7",padding:14,borderRadius:8,display:"flex",gap:10,alignItems:"flex-start",marginTop:16}}>
            <CheckCircle2 size={16}/>
            <div><b>You won this brief · escrow held ₹{brief.awarded_price_inr?.toLocaleString("en-IN")}</b><span style={{display:"block",fontSize:12,color:"#065F46",marginTop:4}}>Deliver drafts below — buyer will request revisions or approve final.</span></div>
          </div>
        )}

        {brief.legacy && (
          <div data-testid="legacy-brief-banner" style={{margin:"12px 0",padding:"10px 12px",borderRadius:8,background:"#F1F5F9",border:"1px solid #CBD5E1",fontSize:12.5,color:"#334155"}}>
            <b>Legacy design job</b>: moved from the old design pipeline (was "{brief.legacy_status}"). Final artwork:
            {(brief.legacy_artwork_urls || []).map((u, i) => <a key={u} data-testid={`legacy-artwork-${i}`} href={u} target="_blank" rel="noreferrer" style={{marginLeft:8}}>File {i + 1} ↗</a>)}
            {brief.linked_job_id && <span style={{marginLeft:8,color:"#64748b"}}>· Print job {brief.linked_job_id}</span>}
          </div>
        )}

        {/* PHASE C — Proofing loop (drafts + revisions + comments + approve) */}
        {!brief.legacy && (brief.status === "in_progress" || brief.status === "approved" || brief.status === "delivered" || (brief.status === "closed" && brief.awarded_agency_id)) &&
          (isBuyerOwner || role === "admin" || (role === "agency" && brief.awarded_agency_id === userId)) && (
          <ProofingPanel
            brief={brief}
            role={role}
            userId={userId}
            onChange={async () => { await reloadBrief(); refresh(); }}
          />
        )}

        {/* PHASE D — Deliverables + pre-press audit + Convert to print order */}
        {!brief.legacy && (brief.status === "approved" || brief.status === "delivered") &&
          (isBuyerOwner || role === "admin" || (role === "agency" && brief.awarded_agency_id === userId)) && (
          <DeliveryPanel
            brief={brief}
            role={role}
            userId={userId}
            onChange={async () => { await reloadBrief(); refresh(); }}
          />
        )}

        {/* BUYER: quotes list + award (bidding open) */}
        {isBuyerOwner && brief.status === "open" && ((brief.hire_mode || "bidding") === "bidding" || quotes.length > 0) && (
          <BuyerQuotesPanel
            brief={brief}
            quotes={quotes}
            user={user}
            tierLabel={tierLabel}
            onAwarded={async () => { await reloadBrief(); await loadQuotes(); refresh(); }}
            setPayErr={setPayErr}
          />
        )}

        {/* BUYER: fixed-rate agency picker */}
        {isBuyerOwner && brief.status === "open" && brief.hire_mode === "fixed" && (
          <BuyerFixedHirePanel
            brief={brief}
            ref_={ref_}
            user={user}
            onAwarded={async () => { await reloadBrief(); refresh(); }}
            setPayErr={setPayErr}
          />
        )}

        {brief.awarded_agency_id && brief.status !== "closed" && brief.dispute_status !== "open" && (
          <BriefDeliveryPanel
            brief={brief}
            role={role}
            userId={userId}
            onChanged={reloadBrief}
          />
        )}

        {(brief.awarded_agency_id || role === "admin" || role === "agency") && (
          <ChatPanel scopeType="brief" scopeId={brief.id} role={role}/>
        )}
      </aside>
    </div>
  );
}
